import { useNavigate } from 'react-router';

import { useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';

interface StartNewActions {
  onFocusGroup?: () => void;
  onContentTest?: () => void;
}

/**
 * Which tools can be started against a set of personas, and how to get there. Each is
 * returned only when its permission and its destination's own guards would both admit the
 * caller, so the menu never offers a jump that lands on a redirect.
 *
 * An empty `personaIds` yields neither: both tools run against personas that exist in the
 * workspace, which is what an audience nobody has saved yet does not have.
 */
export const useStartNewActions = (personaIds: string[], analysisId?: string): StartNewActions => {
  const navigate = useNavigate();
  const { canAccessTestContent, canAccessFocusGroups } = usePermissions();
  const workspaceId = useCurrentWorkspaceId();

  // Content test additionally sits behind a per-workspace tool ACL, which ManagedToolGuard
  // enforces on the route.
  const { isAgentEnabled } = useAgentVisibility(['test-content'], {
    workspaceId,
    organizationId: getCurrentOrganizationId(),
  });

  const hasTargets = personaIds.length > 0;
  // Built by hand rather than through URLSearchParams, which would percent-encode the
  // separator the destination splits on.
  const query = `personaIds=${personaIds.join(',')}${analysisId ? `&analysisId=${analysisId}` : ''}`;

  return {
    onFocusGroup:
      hasTargets && canAccessFocusGroups
        ? () => void navigate(`${ROUTES.TOOLS_FOCUS_GROUPS_NEW}?${query}`)
        : undefined,
    onContentTest:
      hasTargets && canAccessTestContent && isAgentEnabled('test-content')
        ? () => void navigate(`${ROUTES.TOOLS_CONTENT_TEST_NEW}?${query}`)
        : undefined,
  };
};
